import { all, select, call, put } from 'redux-saga/effects';
import axios from 'axios';
import { ALL_FOLDER_ID, SCRAPS_FOLDER_ID } from '@wix/da-url';
import { CommentableTypes } from '@wix/da-shared-react/pkg/Comment/redux/types';
import { normalize } from '@wix/da-shared-react/pkg/redux/normalizr';
import { deviationSchema } from '@wix/da-shared-react/pkg/redux/normalizr/schemas/deviation';
import {
  withOffset,
  selectors as streamSelectors,
} from '@wix/da-shared-react/pkg/Stream';
import { actions as threadActions } from '@wix/da-shared-react/pkg/CommentThread';
import { getProfileOwner, getIsPagedMode } from '../../selectors/users';
import { getFeaturedFolder } from '../../selectors/folders';
import { GallectionType } from '../../../types/gallection';
import { API_BASE_PATH } from '../../../constants';
import { PapiGallectionContentsRequestParams } from '../../../types/papi';
import { biFetchMoreFolderItems } from '../../actions/bi';
import ddt from '@wix/da-ddt';
import { setCsrfOnParams } from '.';
import { isApiError } from '@wix/da-shared-react/pkg/xhr';
import { setPageSafety } from '@wix/da-shared-react/pkg/Ads/redux/actions';
import { getCurrentAdSafety } from '@wix/da-shared-react/pkg/Ads/redux/selectors';

function* fetchFromApi(gallectionType, params) {
  params = yield call(setCsrfOnParams, params);

  if (params.folderId === -1 || params.folderId === '-1') {
    ddt.alert('debug', 'Got -1 in XHR', ['debug'], params);
  }

  return yield axios
    .get(`${API_BASE_PATH}/${gallectionType}/contents`, { params })
    .then(response => response.data)
    .catch(response => false);
}

export default function* fetchMoreFolderDeviations(action) {
  const { streamId } = action;
  const isPaged = yield select(getIsPagedMode);
  if (isPaged) {
    // When pagination is enabled, we need to reload the page after each click
    return;
  }

  yield put(biFetchMoreFolderItems(streamId));
  const [offset, limit, streamParams, profileOwner, currentPageSafety] =
    yield all([
      select(state => withOffset.selectors.getNextOffset(state, streamId)),
      select(state => streamSelectors.getItemsPerFetch(state, streamId)),
      select(state => streamSelectors.getStreamParamsSafe(state, streamId)),
      select(getProfileOwner),
      select(getCurrentAdSafety),
    ]);

  const queryParams: PapiGallectionContentsRequestParams = {
    username: profileOwner.user.username,
    offset,
    limit,
  };

  if (+streamParams.folderId === ALL_FOLDER_ID) {
    queryParams.all_folder = true;
  } else if (+streamParams.folderId === SCRAPS_FOLDER_ID) {
    queryParams.scraps_folder = true;
  } else {
    queryParams.folderid = streamParams.folderId;
  }
  const response = yield call(
    fetchFromApi,
    streamParams.gallectionType,
    queryParams
  );

  const featuredFolder = yield select(getFeaturedFolder);

  let commentableTypeId;
  if (streamParams.gallectionType === GallectionType.COLLECTION) {
    commentableTypeId = CommentableTypes.COMMENT_COLLECTION;
  } else {
    commentableTypeId = CommentableTypes.COMMENT_GALLERY;
  }
  const commentableItemId =
    !streamParams.folderId ||
    [ALL_FOLDER_ID, SCRAPS_FOLDER_ID].includes(streamParams.folderId)
      ? featuredFolder.folderId
      : streamParams.folderId;

  if (response && !isApiError(response)) {
    const deviations = response.results.map(({ deviation }) => deviation);
    const { hasMore, nextOffset, comments } = response;
    const { result, entities } = normalize(deviations, [deviationSchema]);

    if (response?.ads?.unsafe && currentPageSafety !== 'off') {
      yield put(setPageSafety({ safety: response.ads.unsafe }));
    }

    yield put(
      withOffset.actions.fetchSuccess({
        streamId,
        hasMore,
        nextOffset,
        items: result,
        entities,
      })
    );
    if (comments) {
      yield put(
        threadActions.initializeThread({
          thread: {
            commentableTypeId,
            commentableItemId,
            items: comments.thread,
            total: comments.total,
            hasMore: comments.hasMore,
            canPostComment: comments.canPostComment,
            cursor: comments.cursor,
          },
        })
      );
    }
  }

  // TODO error catching
}
